import { AgentModelOption, AgentTypeInfo, ThinkingLevel } from "@/types/agent"

export function filterAllowedModelsForAgentType(
  models: AgentModelOption[],
  agentType: string | null,
  availableAgentTypes?: AgentTypeInfo[]
): AgentModelOption[] {
  const agentTypeInfo = availableAgentTypes?.find((t) => t.name === agentType)
  if (agentTypeInfo?.allowed_model_ids?.length) {
    const byId = new Map(models.map((model) => [model.id, model]))
    return agentTypeInfo.allowed_model_ids
      .map((id) => byId.get(id))
      .filter((model): model is AgentModelOption => Boolean(model))
  }
  return models
}

export function defaultModelIdForAgentType(
  models: AgentModelOption[],
  agentType: string | null,
  availableAgentTypes?: AgentTypeInfo[]
): string | null {
  const allowedModels = filterAllowedModelsForAgentType(
    models,
    agentType,
    availableAgentTypes
  )
  const agentTypeInfo = availableAgentTypes?.find((t) => t.name === agentType)
  const backendDefault = agentTypeInfo?.default_model_id
  if (
    backendDefault &&
    allowedModels.some((model) => model.id === backendDefault)
  ) {
    return backendDefault
  }
  return (
    allowedModels.find((model) => model.recommended)?.id ??
    allowedModels[0]?.id ??
    null
  )
}

export function intersectAllowedModelsForAgentTypes(
  models: AgentModelOption[],
  agentTypes: string[]
): AgentModelOption[] {
  const normalizedTypes = [...new Set(agentTypes.filter(Boolean))]
  if (normalizedTypes.length === 0) return []

  const allowedIdSets = normalizedTypes.map(
    (agentType) =>
      new Set(
        filterAllowedModelsForAgentType(models, agentType).map((m) => m.id)
      )
  )

  const [firstSet, ...restSets] = allowedIdSets
  if (!firstSet) return []

  return models.filter((model) => {
    if (!firstSet.has(model.id)) return false
    return restSets.every((set) => set.has(model.id))
  })
}

export function defaultModelIdForAgentTypes(
  models: AgentModelOption[],
  agentTypes: string[]
): string | null {
  const normalizedTypes = [...new Set(agentTypes.filter(Boolean))]
  if (normalizedTypes.length === 0) return null

  const defaults = normalizedTypes
    .map((agentType) => defaultModelIdForAgentType(models, agentType))
    .filter((id): id is string => Boolean(id))

  if (defaults.length === 0) return null
  if (defaults.every((id) => id === defaults[0])) return defaults[0]

  return null
}

// --- Thinking helpers (moved from evaluation-thinking.ts) ---

export type ThinkingOverridePreset = Exclude<ThinkingLevel, "default">

export const THINKING_OVERRIDE_OPTIONS: ThinkingOverridePreset[] = [
  "off",
  "low",
  "medium",
  "high",
]

export function isGeminiModel(model?: AgentModelOption | null): boolean {
  if (!model) return false
  return model.provider === "google" || model.id.startsWith("gemini_")
}

export function defaultThinkingLevelForModel(
  model?: AgentModelOption | null
): ThinkingOverridePreset {
  const level = model?.default_thinking_level
  if (
    level === "off" ||
    level === "low" ||
    level === "medium" ||
    level === "high"
  ) {
    return level
  }
  return "medium"
}

export function formatThinkingLabel(
  level: ThinkingOverridePreset,
  defaultLevel: ThinkingOverridePreset
): string {
  const title = level.charAt(0).toUpperCase() + level.slice(1)
  return level === defaultLevel ? `${title} (default)` : title
}

export function resolveDefaultThinkingLevel(
  agentType: string | null,
  modelId: string | null,
  availableAgentTypes: AgentTypeInfo[]
): ThinkingOverridePreset {
  const agentTypeInfo = availableAgentTypes.find((t) => t.name === agentType)
  const byModel = agentTypeInfo?.default_thinking_level_by_model_id
  if (modelId && byModel?.[modelId]) {
    return byModel[modelId]
  }
  if (agentTypeInfo?.default_thinking_level) {
    return agentTypeInfo.default_thinking_level
  }
  return "medium"
}

export function asThinkingOverridePreset(
  value: string | null
): ThinkingOverridePreset {
  if (
    value &&
    (THINKING_OVERRIDE_OPTIONS as readonly string[]).includes(value)
  ) {
    return value as ThinkingOverridePreset
  }
  return "medium"
}
